import apiClient, { ApiResponse } from './client';

export interface WebhookConfig {
  id: string;
  organization_id: string;
  name: string | null;
  url: string;
  enabled: boolean;
  notify_on_active_session: boolean;
  notify_on_booked: boolean;
  notify_on_qualified: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateWebhookRequest {
  name?: string;
  url: string;
  secret?: string;
  notify_on_active_session?: boolean;
  notify_on_booked?: boolean;
  notify_on_qualified?: boolean;
}

export interface UpdateWebhookRequest {
  name?: string;
  url?: string;
  secret?: string;
  enabled?: boolean;
  notify_on_active_session?: boolean;
  notify_on_booked?: boolean;
  notify_on_qualified?: boolean;
}

export interface WebhookDelivery {
  id: string;
  organization_id: string;
  session_recording_id: string | null;
  webhook_configuration_id: string | null;
  webhook_name: string | null;
  webhook_url_host: string | null;
  session_email: string | null;
  event_type: string;
  status: string;
  matched_filter: string | null;
  skip_reason: string | null;
  attempt_number: number;
  max_attempts: number;
  http_status_code: number | null;
  response_body_excerpt: string | null;
  error_message: string | null;
  request_payload_sha256: string | null;
  duration_ms: number | null;
  created_at: string;
  delivered_at: string | null;
}

export interface WebhookDeliveryFilters {
  sessionId?: string;
  webhookId?: string;
  status?: string;
  limit?: number;
}

export class WebhooksService {
  async listWebhooks(organizationId: string): Promise<ApiResponse<{ webhooks: WebhookConfig[] }>> {
    return apiClient.get<{ webhooks: WebhookConfig[] }>('/api/webhooks', { organization_id: organizationId });
  }

  async listDeliveries(
    organizationId: string,
    filters: WebhookDeliveryFilters = {},
  ): Promise<ApiResponse<{ deliveries: WebhookDelivery[] }>> {
    return apiClient.get<{ deliveries: WebhookDelivery[] }>('/api/webhooks/deliveries', {
      organization_id: organizationId,
      session_id: filters.sessionId,
      webhook_id: filters.webhookId,
      status: filters.status,
      limit: filters.limit,
    });
  }

  async createWebhook(organizationId: string, data: CreateWebhookRequest): Promise<ApiResponse<WebhookConfig>> {
    return apiClient.post<WebhookConfig>(`/api/webhooks?organization_id=${encodeURIComponent(organizationId)}`, data);
  }

  async updateWebhook(webhookId: string, data: UpdateWebhookRequest): Promise<ApiResponse<WebhookConfig>> {
    return apiClient.put<WebhookConfig>(`/api/webhooks/${webhookId}`, data);
  }

  async deleteWebhook(webhookId: string): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/api/webhooks/${webhookId}`);
  }

  async testWebhook(webhookId: string): Promise<ApiResponse<{ success: boolean; status_code?: number; message?: string }>> {
    return apiClient.post<{ success: boolean; status_code?: number; message?: string }>(`/api/webhooks/${webhookId}/test`);
  }
}

const webhooksService = new WebhooksService();
export default webhooksService;
